import * as React from "react";
import { Input } from "@/components/ui/input";
import { ClipboardText } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Input with a small clipboard-paste button on the right. Useful in mobile /
 * iPad where the native long-press → paste menu can be unreliable, and as a
 * convenience on desktop too.
 *
 * Pass any prop you would pass to `<Input>` (value, onChange, type, etc).
 * On click, reads `navigator.clipboard.readText()` and fires an onChange
 * event with the pasted value, replacing the current content. The button is
 * hidden when the input is disabled or readOnly.
 */
export const PasteableInput = React.forwardRef(function PasteableInput(
  { className = "", onChange, disabled, readOnly, ...rest },
  ref,
) {
  const inputRef = React.useRef(null);
  const handlePaste = async (e) => {
    e.preventDefault();
    try {
      const text = await navigator.clipboard.readText();
      if (!text) {
        toast.info("El portapapeles está vacío");
        return;
      }
      const trimmed = text.trim();
      if (onChange) {
        // Fake a regular change event so existing handlers work without changes.
        onChange({ target: { value: trimmed } });
      }
      // Restore focus to the input for further edits.
      const el = (typeof ref === "function" ? null : ref?.current) || inputRef.current;
      el?.focus?.();
    } catch (err) {
      toast.error("No se pudo acceder al portapapeles. Permite el acceso en tu navegador o usa Ctrl/Cmd+V.");
    }
  };

  return (
    <div className="relative">
      <Input
        ref={(el) => {
          inputRef.current = el;
          if (typeof ref === "function") ref(el);
          else if (ref) ref.current = el;
        }}
        className={`pr-9 ${className}`}
        onChange={onChange}
        disabled={disabled}
        readOnly={readOnly}
        {...rest}
      />
      {!disabled && !readOnly && (
        <button
          type="button"
          onClick={handlePaste}
          tabIndex={-1}
          aria-label="Pegar desde el portapapeles"
          title="Pegar"
          data-testid="paste-btn"
          className="absolute right-1 top-1/2 -translate-y-1/2 p-1.5 text-zinc-400 hover:text-[#002FA7] hover:bg-zinc-100 rounded transition-colors"
        >
          <ClipboardText size={14} weight="bold" />
        </button>
      )}
    </div>
  );
});

export default PasteableInput;
